import styles from './regulation-28-table.module.scss';
import { useEffect, useState } from 'react';
import {
  DIRECTION,
  IDirection,
  Table,
  TableBody,
  TableCell,
  TablePagination
} from '@sanlam-glacier/app-common/components';
import clsx from 'clsx';
import {
  Reg28TableHeaderConfig,
  Regulation28TableHeadings,
  Regulation28HeaderKeys
} from '../../../types/regulation-28/regulation-28-table.type';
import { Regulation28TableHeader } from './Regulation28TableHeader';
import { Regulation28TableRow } from './Regulation28TableRow';
import { Reg28ClientBreachesResponseModelMock } from '../../../types/regulation-28/regulation-28.interfaces';

const Regulation28Table = ({ entries }: { entries: Reg28ClientBreachesResponseModelMock[] }) => {
  const testComponentName = 'regulation28Table';

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const [regulation28TableHeaderData, setRegulation28TableHeaderData] = useState<
    Reg28TableHeaderConfig<Regulation28TableHeadings>[]
  >([]);

  useEffect(() => {
    const intialHeaderData = [
      {
        key: Regulation28HeaderKeys.clientName,
        label: 'Client name',
        direction: DIRECTION.ASC,
        className: clsx(styles.tableHead)
      },
      {
        key: Regulation28HeaderKeys.clientId,
        label: 'Client ID',
        direction: DIRECTION.ASC,
        className: clsx(styles.tableHead)
      },
      {
        key: Regulation28HeaderKeys.productType,
        label: 'Types of product',
        direction: DIRECTION.ASC,
        className: clsx(styles.tableHead, styles.typeOfProduct)
      },
      {
        key: Regulation28HeaderKeys.totalDaysNonCompliant,
        direction: DIRECTION.ASC,
        label: 'Days not compliant',
        className: clsx(styles.tableHead, styles.daysNotCompliant)
      },
      {
        label: 'Exposure breach',
        className: clsx(styles.tableHead)
      },
      {
        label: 'Exposure breakdown',
        className: clsx(styles.tableHead, styles.exposureBreakdown)
      }
    ];
    setRegulation28TableHeaderData(intialHeaderData);
  }, []);

  const toggleDirection = (direction: IDirection): IDirection => {
    return direction === DIRECTION.ASC ? DIRECTION.DESC : DIRECTION.ASC;
  };

  const handleSort = (
    property: Reg28TableHeaderConfig<Regulation28TableHeadings>['key'] | string,
    direction: IDirection | undefined
  ) => {
    if (direction) {
      const newDirection = toggleDirection(direction);

      setRegulation28TableHeaderData((prev) =>
        prev.map((header) => {
          if (header.key === property) {
            return { ...header, direction: newDirection };
          }
          return header;
        })
      );

      switch (property) {
        case Regulation28HeaderKeys.clientName:
          entries.sort((a, b) =>
            newDirection === DIRECTION.DESC
              ? a.clientName.localeCompare(b.clientName)
              : b.clientName.localeCompare(a.clientName)
          );
          break;
        case Regulation28HeaderKeys.clientId:
          entries.sort((a, b) =>
            newDirection === DIRECTION.DESC
              ? a.clientId.localeCompare(b.clientId)
              : b.clientId.localeCompare(a.clientId)
          );
          break;
        case Regulation28HeaderKeys.productType:
          entries.sort((a, b) =>
            newDirection === DIRECTION.DESC
              ? a.productType.localeCompare(b.productType)
              : b.productType.localeCompare(a.productType)
          );
          break;
        case Regulation28HeaderKeys.totalDaysNonCompliant:
          entries.sort((a, b) =>
            newDirection === DIRECTION.DESC
              ? a.totalDaysNonCompliant - b.totalDaysNonCompliant
              : b.totalDaysNonCompliant - a.totalDaysNonCompliant
          );
          break;
        default:
          break;
      }
    }
  };

  const paginatedData = entries.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <>
      <Table data-testid={`${testComponentName}`} size={Table.SIZE.SMALL}>
        <Regulation28TableHeader
          headers={regulation28TableHeaderData}
          align={TableCell.ALIGN.LEFT}
          handleSort={handleSort}
          tableHeaderClassName={styles.tableSortHeaderLabel}
        />
        <TableBody data-testid={`${testComponentName}Body`}>
          {paginatedData.map((row) => (
            <Regulation28TableRow key={row.clientId} row={row} />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        data-testid={`${testComponentName}Pagination`}
        className={styles.tableFooter}
        count={entries.length}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(newPage: number) => setPage(newPage)}
        onRowsPerPageChange={(newPageSize: number) => {
          setPage(0);
          setPageSize(newPageSize);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        closeMenuOnSelection
        showLabels={false}
      />
    </>
  );
};

export default Regulation28Table;
